import { useMemo } from 'react';

import config from 'config';

import { buildMasterProductGroupCodeObjects } from '~/shared/utils/buildMasterProductGroupCodeObjects';

import { mergeUnifiedPrimaryProductsAndRelatedData } from '../transformers/mergeUnifiedPrimaryProductsAndRelatedData';
import { NormalizedCioResponseInfiniteData } from '../transformers/normalizeCioResponse';
import { GridProviderResponse } from '../types/gridResponses';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useCioBrowseFacets from './useCioBrowseFacets';
import useCioBrowseProducts from './useCioBrowseProducts';
import useFacetTracking from './useFacetTracking';

type CIOBrowseProductsResponse = {
  data: NormalizedCioResponseInfiniteData<any>;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  cioFilterName: string | null;
  cioFilterValue: string | null;
  selectedSortOption: string | null;
  customFacetFilters: Record<string, any>;
  setCustomFacetFilters: (filters: Record<string, any>) => void;
};

const facetBlacklist = config.get<string[]>(
  'public.brand.constructor.facetBlacklist'
);

const useCioBrowseAndGqlProducts = (): GridProviderResponse => {
  const { selectedUrlFilters, allRegisteredFacets, allRegisteredFacetNames } =
    useCioBrowseFacets();

  const {
    data: cioBrowseResults,
    isLoading: isPrimaryProductsLoading,
    isFetching: isPrimaryProductsFetching,
    // error,
    fetchNextPage,
    hasNextPage,
    cioFilterName,
    cioFilterValue,
    selectedSortOption,
    customFacetFilters,
    setCustomFacetFilters,
  } = useCioBrowseProducts({
    selectedUrlFilters,
    allRegisteredFacets,
  }) as CIOBrowseProductsResponse;

  const primaryProducts = cioBrowseResults?.products;

  const masterProductIds = useMemo(() => {
    return primaryProducts?.map((result) => result?.masterProductId);
  }, [primaryProducts]);

  // dedupe by groupCode, merging masterProductIds
  const groupCodeObjects = useMemo(
    () => buildMasterProductGroupCodeObjects(primaryProducts ?? []),
    [primaryProducts]
  );

  const {
    data: pricingData,
    isLoading: isProductPricingLoading,
    isFetching: isProductPricingFetching,
  } = useBatchedGqlProductPricing(masterProductIds);
  const {
    data: relatedProductsData,
    isLoading: isRelatedProductsLoading,
    isFetching: isRelatedProductsFetching,
  } = useBatchedGqlRelatedProducts(groupCodeObjects);

  // Combine the primary products with the pricingData and relatedProductsData
  const mergedProducts = useMemo(
    () =>
      mergeUnifiedPrimaryProductsAndRelatedData({
        primaryProducts,
        pricingData,
        relatedProductsData,
      }),
    [primaryProducts, pricingData, relatedProductsData]
  );

  // Derive page-0 values from the first page only so that appending new pages
  // during infinite scroll does NOT create new references for facets, sort
  // options, or totalNumResults — preventing unnecessary FacetsContext updates.
  const firstPage = cioBrowseResults?.pages?.[0];

  const totalNumResults = firstPage?.totalNumResults;

  const facets = useMemo(() => {
    const rawFacets = firstPage?.response?.facets;
    return rawFacets?.filter((facet) => !facetBlacklist.includes(facet.name));
  }, [firstPage]);

  const sortOptions = useMemo(() => {
    return firstPage?.response?.sort_options;
  }, [firstPage]);

  useFacetTracking({ selectedUrlFilters, selectedSortOption });

  return {
    type: 'cio-browse' as const,
    products: mergedProducts,
    fetchNextPage,
    hasNextPage,
    facets,
    sortOptions,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    totalNumResults,
    isPrimaryProductsLoading,
    isPrimaryProductsFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    cioFilterName,
    cioFilterValue,
    customFacetFilters,
    setCustomFacetFilters,
  };
};

export default useCioBrowseAndGqlProducts;
